import DeliveryModeSection from "./DeliveryModeSection";
import CustomPopUp from "../global/CustomPopup";
import Button from "../organism/Button";
import {
  CommunicationChannel,
  DeliveryMode,
  WeekDay,
} from "../../constants/communicationConstants";
import { PreferenceSchedule } from "../../services/models/communication/communicationPreferences.model";
import NotificationSettingsTableLoadingIndicator from "../global/LoadingIndicators/NotificationsSettingsTableLoadingIndicator";
import ChannelSelector from "./ChannelSelector";

interface CustomizeOrganizationModalProps {
  organizationName: string;
  draft: PreferenceSchedule | null;
  personalDefaults: PreferenceSchedule;
  useDefaults: boolean;
  email?: string | null;
  mobilePhone?: string | null;
  fax?: string | null;
  mobilePhoneVerified?: boolean;
  hasFaxNumber?: boolean;
  isLoading?: boolean;
  isSaving?: boolean;
  onChange: (next: PreferenceSchedule) => void;
  onUseDefaults: () => void;
  onCustomizeInstead: () => void;
  onCancel: () => void;
  onSave: () => void;
  onAddVerifyPhone?: () => void;
}

function CustomizeOrganizationModal({
  organizationName,
  draft,
  personalDefaults,
  useDefaults,
  email,
  mobilePhone,
  fax,
  mobilePhoneVerified,
  hasFaxNumber,
  isLoading = false,
  isSaving = false,
  onChange,
  onUseDefaults,
  onCustomizeInstead,
  onCancel,
  onSave,
  onAddVerifyPhone,
}: CustomizeOrganizationModalProps): JSX.Element {
  return (
    <CustomPopUp
      popUpTitle={`My preferences for ${organizationName}`}
      popUpSubtitle={`Only your notifications for ${organizationName} will change.`}
      hidePopUp={onCancel}
      warningPopupNotificationSettings
      subTitleClassName="!my-1 text-textSecondaryGray"
    >
      {isLoading || !draft ? (
        <NotificationSettingsTableLoadingIndicator />
      ) : (
        <div className="max-h-[65vh] overflow-auto pr-1">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 pb-4 border-b border-dropDownBorder">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="orgPreferenceMode"
                checked={useDefaults}
                disabled={isSaving}
                onChange={onUseDefaults}
                className="h-4 w-4 accent-primary"
              />
              <span className="text-sm font-medium text-royalBlue">
                Use my defaults
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="orgPreferenceMode"
                checked={!useDefaults}
                disabled={isSaving}
                onChange={onCustomizeInstead}
                className="h-4 w-4 accent-primary"
              />
              <span className="text-sm font-medium text-royalBlue">
                Customize for this organization
              </span>
            </label>
          </div>

          {useDefaults ? (
            <div className="py-5 space-y-2 text-sm text-royalBlue">
              <p className="font-semibold">Your default preferences</p>
              <p className="text-textSecondaryGray">
                Channels:{" "}
                {personalDefaults.channels
                  .map((channel) =>
                    channel === CommunicationChannel.SMS
                      ? "SMS"
                      : channel.charAt(0).toUpperCase() + channel.slice(1)
                  )
                  .join(", ") || "None"}
              </p>
              <p className="text-textSecondaryGray">
                Delivery:{" "}
                {personalDefaults.deliveryMode === DeliveryMode.REAL_TIME
                  ? "As they happen"
                  : `Scheduled summary (${personalDefaults.summaryFrequency})`}
              </p>
              <p className="text-xs text-textSecondaryGray pt-1">
                This organization will inherit your default preferences after
                save.
              </p>
            </div>
          ) : (
            <>
              <section className="py-5 border-b border-dropDownBorder">
                <h4 className="font-semibold text-royalBlue mb-3">Channels</h4>
                <ChannelSelector
                  channels={draft.channels}
                  onChange={(channels:any) => onChange({ ...draft, channels })}
                  email={email}
                  mobilePhone={mobilePhone}
                  fax={fax}
                  mobilePhoneVerified={mobilePhoneVerified}
                  hasFaxNumber={hasFaxNumber}
                  onAddVerifyPhone={onAddVerifyPhone}
                />
              </section>

              <section className="py-5">
                <h4 className="font-semibold text-royalBlue mb-3">Delivery</h4>
                <DeliveryModeSection
                  deliveryMode={draft.deliveryMode}
                  summaryTime={draft.summaryTime}
                  summaryFrequency={draft.summaryFrequency}
                  summaryDayOfWeek={draft.summaryDayOfWeek}
                  timezone={draft.timezone}
                  radioGroupName="orgCustomizeDeliveryMode"
                  footerNote={`Applies to all notification types for ${organizationName}.`}
                  onDeliveryModeChange={(nextDeliveryMode) =>
                    onChange({ ...draft, deliveryMode: nextDeliveryMode })
                  }
                  onSummaryTimeChange={(nextSummaryTime) =>
                    onChange({ ...draft, summaryTime: nextSummaryTime })
                  }
                  onSummaryFrequencyChange={(nextSummaryFrequency) =>
                    onChange({
                      ...draft,
                      summaryFrequency: nextSummaryFrequency,
                    })
                  }
                  onSummaryDayOfWeekChange={(nextDay: WeekDay) =>
                    onChange({ ...draft, summaryDayOfWeek: nextDay })
                  }
                  onTimezoneChange={(nextTimezone) =>
                    onChange({ ...draft, timezone: nextTimezone })
                  }
                />
              </section>
            </>
          )}
        </div>
      )}

      <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-dropDownBorder pt-4 mt-2">
        <p className="text-xs text-textSecondaryGray">
          Other organizations keep using your defaults.
        </p>
        <div className="flex items-center justify-end gap-3 shrink-0">
          <Button
            buttonTheme="secondary"
            textInformation={{ text: "Cancel", fontWeight: "font-semibold" }}
            onClick={onCancel}
            disabled={isSaving}
          />
          <Button
            buttonTheme="primary"
            textInformation={{
              text: isSaving ? "Saving..." : "Save",
              fontWeight: "font-semibold",
            }}
            onClick={onSave}
            disabled={
              isLoading ||
              isSaving ||
              !draft ||
              (!useDefaults && draft.channels.length === 0)
            }
            loading={isSaving}
          />
        </div>
      </div>
    </CustomPopUp>
  );
}

export default CustomizeOrganizationModal;
